import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Shield, Users } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import type { Tables } from '@/integrations/supabase/types';
import { useUserRole } from '@/hooks/useUserRole';
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppFieldGroup,
  AppHeaderBar,
  AppInput,
  AppLoadingState,
  AppPanelButton,
  AppPage,
  AppSectionHeader,
  AppText,
} from '../../ui-foundation';
import {
  AppAvatar as Avatar,
  AppAvatarFallback as AvatarFallback,
  AppAvatarImage as AvatarImage,
} from '../../ui-foundation/adapters/web';

type MemberRow = Pick<
  Tables<'profiles'>,
  'user_id' | 'first_name' | 'postcode' | 'avatar_url' | 'community_access_status' | 'created_at'
>;

type AdminMemberDirectoryRow = MemberRow & {
  email: string | null;
};

type RoleRow = Pick<Tables<'user_roles'>, 'user_id' | 'role'>;

function getRoleTone(role: RoleRow['role'] | 'user') {
  if (role === 'admin') return 'bg-primary/10 text-primary';
  if (role === 'moderator') return 'bg-secondary/10 text-secondary-foreground';
  return 'bg-muted text-muted-foreground';
}

function getStatusTone(status: MemberRow['community_access_status']) {
  if (status === 'suspended') return 'bg-amber-100 text-amber-800';
  if (status === 'banned') return 'bg-destructive/10 text-destructive';
  return 'bg-green-100 text-green-800';
}

function getMemberDisplayLabel(member: Pick<AdminMemberDirectoryRow, 'first_name' | 'email'>) {
  const firstName = member.first_name?.trim();
  if (firstName) return firstName;

  const emailLocalPart = member.email?.split('@')[0]?.trim();
  if (emailLocalPart) return emailLocalPart;

  return 'Adulting member';
}

export default function AdminMembersPage() {
  const navigate = useNavigate();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [members, setMembers] = useState<AdminMemberDirectoryRow[]>([]);
  const [roles, setRoles] = useState<Record<string, RoleRow['role']>>({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!roleLoading && !isAdmin) {
      navigate('/admin', { replace: true });
    }
  }, [isAdmin, navigate, roleLoading]);

  const loadMembers = useCallback(async () => {
    if (!isAdmin) return;

    setLoading(true);
    const [{ data: profiles, error: profileError }, { data: userRoles, error: roleError }] = await Promise.all([
      supabase.rpc('get_admin_members_directory'),
      supabase.from('user_roles').select('user_id, role'),
    ]);

    if (profileError || roleError) {
      console.error(profileError || roleError);
      setMembers([]);
      setRoles({});
      setLoading(false);
      return;
    }

    const nextRoles: Record<string, RoleRow['role']> = {};
    ((userRoles || []) as RoleRow[]).forEach((entry) => {
      nextRoles[entry.user_id] = entry.role;
    });

    setMembers((profiles || []) as AdminMemberDirectoryRow[]);
    setRoles(nextRoles);
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  const filteredMembers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return members;

    return members.filter((member) => {
      const name = (member.first_name || '').toLowerCase();
      const email = (member.email || '').toLowerCase();
      const postcode = (member.postcode || '').toLowerCase();
      return name.includes(query) || email.includes(query) || postcode.includes(query);
    });
  }, [members, search]);

  return (
    <AppPage>
      <AppHeaderBar
        title="User admin"
        action={(
          <AppButton type="button" tone="outline" size="sm" onClick={() => navigate('/admin')}>
            <ArrowLeft size={16} />
            Back
          </AppButton>
        )}
      />

      <div className="space-y-6">
        <AppCard>
          <AppCardBody>
            <AppSectionHeader title="Find a member" subtitle="Search by first name, email, or postcode." />
            <AppFieldGroup label="Search">
              <div className="relative">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <AppInput
                  value={search}
                  onChangeText={setSearch}
                  placeholder="Search by name, email, or postcode"
                  className="pl-9"
                />
              </div>
            </AppFieldGroup>
          </AppCardBody>
        </AppCard>

        {loading ? (
          <AppLoadingState title="Loading members" body="Pulling member profiles, role assignments, and current access status." />
        ) : filteredMembers.length === 0 ? (
          <AppCard>
            <AppCardBody>
              <AppText>{search.trim() ? 'No members matched that search.' : 'No members are available right now.'}</AppText>
            </AppCardBody>
          </AppCard>
        ) : (
          <div className="space-y-3">
            {filteredMembers.map((member) => {
              const role = roles[member.user_id] || 'user';
              const memberLabel = getMemberDisplayLabel(member);

              return (
                <AppPanelButton
                  key={member.user_id}
                  onClick={() => navigate(`/admin/community-members/${member.user_id}`)}
                  className="w-full rounded-xl p-0 text-left"
                >
                  <AppCard className="transition-colors hover:border-primary/40 hover:bg-primary/5">
                    <AppCardBody className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex min-w-0 items-center gap-4">
                        <Avatar className="h-12 w-12 border border-border/70 bg-background">
                          {member.avatar_url ? <AvatarImage src={member.avatar_url} alt={memberLabel} /> : null}
                          <AvatarFallback className="bg-primary/15 text-primary font-semibold">
                            {memberLabel.trim().charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground">{memberLabel}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {member.postcode || 'No postcode saved'} · Joined {new Date(member.created_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getRoleTone(role)}`}>
                          {role === 'admin' ? 'Admin' : role === 'moderator' ? 'Moderator' : 'Member'}
                        </span>
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getStatusTone(member.community_access_status)}`}>
                          {member.community_access_status === 'active'
                            ? 'Active'
                            : member.community_access_status === 'suspended'
                              ? 'Suspended'
                              : 'Banned'}
                        </span>
                        <Shield size={16} className="text-primary" />
                      </div>
                    </AppCardBody>
                  </AppCard>
                </AppPanelButton>
              );
            })}
          </div>
        )}
      </div>
    </AppPage>
  );
}
